// src/catalog/utils/catalogLaunch.js

import { canLaunchCatalogEntry } from "./catalogAccess";

function isCAPEVEntry(entry) {
  const instrument = entry?.metadata?.instrument;

  return (
    instrument?.instrument === "CAPE-V" ||
    instrument?.baseInstrument === "CAPE-V"
  );
}

function getExternalLaunchTarget(entry, launch) {
  if (entry?.primaryType === "resource") {
    return "newTab";
  }

  return launch?.target || "newTab";
}

function appendQueryParam(route, key, value) {
  if (!route || !key || value === null || value === undefined || value === "") {
    return route;
  }

  const [routeWithoutHash, hash] = String(route).split("#");
  const [pathname, search = ""] = routeWithoutHash.split("?");
  const params = new URLSearchParams(search);

  params.set(key, value);

  const query = params.toString();
  const hashSuffix = hash ? `#${hash}` : "";

  return `${pathname}${query ? `?${query}` : ""}${hashSuffix}`;
}

function appendQueryParamsFromRoute(route, sourceRoute) {
  if (!route || !sourceRoute) {
    return route;
  }

  const [, sourceSearch = ""] = String(sourceRoute).split("?");

  if (!sourceSearch) {
    return route;
  }

  const [searchWithoutHash] = sourceSearch.split("#");
  const sourceParams = new URLSearchParams(searchWithoutHash);
  let nextRoute = route;

  sourceParams.forEach((value, key) => {
    nextRoute = appendQueryParam(nextRoute, key, value);
  });

  return nextRoute;
}

function buildCatalogAppViewLaunchRoute(entry, options = {}) {
  if (!entry?.slug) {
    return null;
  }

  const routeWithSourceParams = appendQueryParamsFromRoute(
    `/app/${entry.slug}/open`,
    entry.launch?.route
  );

  return applyCatalogLaunchOptions(
    routeWithSourceParams,
    entry,
    options
  );
}

function applyCatalogLaunchOptions(route, entry, options = {}) {
  if (!route) {
    return route;
  }

  let nextRoute = route;

  if (isCAPEVEntry(entry) && options.adaptationId) {
    nextRoute = appendQueryParam(
      nextRoute,
      "adaptation",
      options.adaptationId
    );
  }

  if (options.launchConfigurationId) {
    nextRoute = appendQueryParam(
      nextRoute,
      "launchConfig",
      options.launchConfigurationId
    );
  }

  if (options.programLocale) {
    nextRoute = appendQueryParam(
      nextRoute,
      "programLocale",
      options.programLocale
    );
  }

  return nextRoute;
}

export function resolveCatalogLaunch(entry, options = {}) {
  if (!entry) return null;

  if (!canLaunchCatalogEntry(entry)) {
    return null;
  }

  if (
    entry.lifecycleStatus === "comingSoon" ||
    entry.lifecycleStatus === "closed" ||
    entry.lifecycleStatus === "archived" ||
    entry.participationMode === "closed" ||
    entry.participationMode === "archived"
  ) {
    return null;
  }

  if (entry.launch?.type === "appView") {
    const catalogLaunchRoute =
      buildCatalogAppViewLaunchRoute(entry, options);

    if (catalogLaunchRoute) {
      return {
        type: "route",
        to: catalogLaunchRoute
      };
    }

    if (entry.launch.viewId) {
      return {
        type: "route",
        to: applyCatalogLaunchOptions(
          `/app/${entry.launch.viewId}`,
          entry,
          options
        )
      };
    }
  }

  if (entry.launch?.type === "project") {
    if (entry.slug && entry.source?.slug) {
      return {
        type: "route",
        to: applyCatalogLaunchOptions(
          `/app/${entry.slug}/open`,
          entry,
          options
        )
      };
    }
  }

  if (
    entry.launch?.type === "externalUrl" &&
    entry.launch.externalUrl
  ) {
    return {
      type: "external",
      url: entry.launch.externalUrl,
      target: getExternalLaunchTarget(entry, entry.launch)
    };
  }

  if (entry.source?.type === "project" && entry.source?.slug) {
    return {
      type: "route",
      to: entry.slug
        ? `/app/${entry.slug}/open`
        : `/app/project/${entry.source.slug}`
    };
  }

  if (entry.primaryType === "service") {
    return {
      type: "route",
      to: "/contact"
    };
  }

  return null;
}

export function openCatalogLaunch(entry, navigate, options = {}) {
  if (!entry) return false;

  if (!canLaunchCatalogEntry(entry)) {
    return false;
  }

  const launch = entry.launch || {};

  if (launch.type === "appView") {
    const catalogLaunchRoute =
      buildCatalogAppViewLaunchRoute(entry, options);

    if (catalogLaunchRoute) {
      navigate(catalogLaunchRoute);
      return true;
    }
  }

  if (
    launch.type === "project" &&
    entry.slug &&
    entry.source?.slug
  ) {
    navigate(applyCatalogLaunchOptions(
      `/app/${entry.slug}/open`,
      entry,
      options
    ));
    return true;
  }

  if (launch.type === "project") {
    return false;
  }

  if (launch.route) {
    navigate(applyCatalogLaunchOptions(launch.route, entry, options));
    return true;
  }

  if (launch.type === "appView" && launch.viewId) {
    navigate(
      applyCatalogLaunchOptions(
        `/app/${launch.viewId}`,
        entry,
        options
      )
    );
    return true;
  }

  if (launch.type === "externalUrl" && launch.externalUrl) {
    window.open(
      launch.externalUrl,
      getExternalLaunchTarget(entry, launch) === "sameTab" ? "_self" : "_blank",
      "noopener,noreferrer"
    );

    return true;
  }

  return false;
}
